/**
 * Buffers recent console output for support reports (AppToast copy / email).
 * Installed once from main.ts; read via getSupportReportConsoleSection().
 */

const MAX_LINES = 120;
const MAX_LINE_CHARS = 4000;
/** Keep total size bounded for mailto URLs and clipboard. */
const MAX_BUFFER_CHARS = 18000;

const lines: string[] = [];
let patched = false;

function formatArg(arg: unknown): string {
  if (arg instanceof Error) {
    const stack = arg.stack || "";
    return stack.length > 0 ? `${arg.message}\n${stack}` : arg.message;
  }
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

/** Record noisy console.log only when it looks like a failed request / hard failure. */
function shouldCaptureLogLine(args: unknown[]): boolean {
  const s = args.map(formatArg).join(" ").toLowerCase();
  return (
    /\b(5\d\d|4\d\d)\b/.test(s) ||
    /internal server error/i.test(s) ||
    /network error/i.test(s) ||
    /request failed/i.test(s) ||
    /post\s+https?:/i.test(s) ||
    /get\s+https?:/i.test(s) ||
    /put\s+https?:/i.test(s) ||
    /patch\s+https?:/i.test(s)
  );
}

function pushEntry(level: string, args: unknown[]): void {
  try {
    const text = args.map(formatArg).join(" ").replace(/\s+/g, " ").trim();
    const clipped =
      text.length > MAX_LINE_CHARS ? `${text.slice(0, MAX_LINE_CHARS)}…` : text;
    const stamp = new Date().toISOString();
    lines.push(`[${stamp}] ${level} ${clipped}`);
    while (lines.length > MAX_LINES) {
      lines.shift();
    }
    while (lines.length > 0 && lines.join("\n").length > MAX_BUFFER_CHARS) {
      lines.shift();
    }
  } catch {
    /* ignore capture failures */
  }
}

/** Call once at app bootstrap (before or right after createApp). */
export function installConsoleCaptureForSupport(): void {
  if (patched || typeof window === "undefined") return;
  patched = true;

  const c = console;
  const origError = c.error.bind(c);
  const origWarn = c.warn.bind(c);
  const origLog = c.log.bind(c);

  c.error = (...args: unknown[]) => {
    pushEntry("ERROR", args);
    origError(...args);
  };

  c.warn = (...args: unknown[]) => {
    pushEntry("WARN", args);
    origWarn(...args);
  };

  c.log = (...args: unknown[]) => {
    if (shouldCaptureLogLine(args)) {
      pushEntry("LOG", args);
    }
    origLog(...args);
  };

  window.addEventListener("error", (ev: ErrorEvent) => {
    const head = [ev.message || "Error"]
      .concat(ev.filename ? [`${ev.filename}:${ev.lineno}:${ev.colno}`] : [])
      .join(" @ ");
    pushEntry("WINDOW_ERROR", [head, ev.error ?? undefined]);
  });

  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    pushEntry("UNHANDLED_REJECTION", [ev.reason]);
  });
}

/**
 * Returns a non-empty block for the support report, or "" if nothing captured.
 */
export function getSupportReportConsoleSection(title: string): string {
  if (lines.length === 0) return "";
  return `${title}\n${lines.join("\n")}`;
}
